import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Layers3, LayoutDashboard, Users, ShieldCheck, Send, Settings, FlaskConical, BookOpen, UserCog, Rocket, Megaphone } from 'lucide-react';
import { t } from '../utils/i18n';
import { NAV_GROUPS, filterNavigation } from './navigationConfig';
import { useAuth } from '../context/AuthContext';
import { isAccountManager } from '../utils/adminPermissions';

const ICONS = { products: Layers3, ads: Megaphone, overview: LayoutDashboard, people: Users, moderation: ShieldCheck, push: Send, settings: Settings, tools: FlaskConical, resources: BookOpen, administration: UserCog, release: Rocket };

export default function Navigation({ activeItem, isTest, onNavigate, idPrefix }) {
  const { user } = useAuth();
  const canManageAccounts = isAccountManager(user);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const groups = filterNavigation(NAV_GROUPS, search, t, isTest, canManageAccounts);
  const navigationRef = useRef(null);
  useEffect(() => {
    const nav = navigationRef.current;
    const selected = nav.querySelector('[aria-current="page"]');
    if (!selected) return;
    const revealSelected = () => {
      const navBounds = nav.getBoundingClientRect();
      const selectedBounds = selected.getBoundingClientRect();
      if (selectedBounds.bottom > navBounds.bottom || selectedBounds.top < navBounds.top) {
        nav.scrollTop += selectedBounds.top - navBounds.top - nav.clientHeight / 2 + selectedBounds.height / 2;
      }
    };
    revealSelected();
    const resizeObserver = new ResizeObserver(revealSelected);
    resizeObserver.observe(nav);
    return () => resizeObserver.disconnect();
  }, [activeItem?.to]);
  const renderLink = (item, nested = false) => {
    const active = item.to === activeItem?.to;
    const props = {
      className: `admin-nav-link ${nested ? 'admin-nav-child' : ''} ${active ? 'is-active' : ''}`,
      'aria-current': active ? 'page' : undefined,
      onClick: onNavigate,
    };
    return item.isExternal ? <a key={item.to} href={item.to} {...props}>{t(item.key)}</a> : <Link key={item.to} to={item.to} {...props}>{t(item.key)}</Link>;
  };
  return (
    <nav ref={navigationRef} aria-label={t('header.navigation.label')} className="admin-navigation">
      <label className="admin-nav-search"><span className="sr-only">{t('adminUi.searchMenu')}</span><input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder={t('adminUi.searchPlaceholder')} /></label>
      {groups.length === 0 && <p className="p-3 text-sm text-dark-text-secondary">{t('adminUi.noMenus')}</p>}
      {groups.map(({ id, items }) => {
        const groupItems = items;
        if (groupItems.length === 0) return null;
        const Icon = ICONS[id];
        return <section key={id} aria-labelledby={`${idPrefix}-${id}`}>
          <h2 id={`${idPrefix}-${id}`} className="admin-nav-heading"><Icon size={14} aria-hidden="true" />{t(`header.navigation.${id}`)}</h2>
          {groupItems.map((item) => {
            if (!item.children) return renderLink(item);
            const children = item.children;
            const active = item.to === activeItem?.to || children.some((child) => child.to === activeItem?.to);
            const open = Boolean(search.trim()) || (expanded[item.key] ?? active);
            const panelId = `${idPrefix}-${item.key.replaceAll('.', '-')}`;
            return <div key={item.key}>
              {item.to === '/ads' ? <div className="flex items-center">{renderLink(item)}<button type="button" className="p-2" aria-label="Ads 하위 메뉴" aria-expanded={open} aria-controls={panelId} onClick={() => setExpanded(value => ({ ...value, [item.key]: !open }))}><ChevronDown size={16} className={open ? 'rotate-180' : ''} /></button></div> : <button type="button" className={`admin-nav-link admin-nav-parent ${active ? 'has-active' : ''}`} aria-expanded={open} aria-controls={panelId}
                onClick={() => setExpanded((value) => ({ ...value, [item.key]: !open }))}>
                <span>{t(item.key)}</span>
                <ChevronDown size={16} className={open ? 'rotate-180' : ''} aria-hidden="true" />
              </button>}
              <div id={panelId} hidden={!open}>{children.map((child) => renderLink(child, true))}</div>
            </div>;
          })}
        </section>;
      })}
    </nav>
  );
}
